'use client';

import React from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { appendQueryStringToUrl } from '@/utils/url-helper';
import { FormatCurrency } from '@/ui/hooks/useCurrencyFormatter';
import Button from '../Button';
import { SharedButtonLinkProps } from './types';
import { CtaPrice } from './cta-price';
import { parseProductVariantIntoLink } from './utils/parse-product-variant-into-link';

type ProductVariant = Parameters<typeof parseProductVariantIntoLink>[0];

interface ProductVariantButtonProps extends SharedButtonLinkProps {
  productVariant: ProductVariant;
  pricePosition?: string | null;
  currentPath: string;
  openInNewTab?: boolean | null;
  className?: string;
  applyClassesToButton?: boolean;
  urlParamsQueryString: string;
  formatCurrency: FormatCurrency;
}

export default function ProductVariantButton({
  productVariant,
  pricePosition,
  currentPath,
  openInNewTab,
  className,
  applyClassesToButton,
  urlParamsQueryString,
  formatCurrency,
  text,
  children,
  variant,
  noUppercase,
  ariaLabel,
  tileElementTrackingId,
  hasExternalAreaLink,
  onClick,
  modal,
}: Readonly<ProductVariantButtonProps>) {
  const parsedLink = parseProductVariantIntoLink(productVariant, currentPath, true);
  const href = parsedLink?.href
    ? appendQueryStringToUrl(parsedLink.href, urlParamsQueryString)
    : undefined;

  const price = (
    <CtaPrice priceVariant={productVariant.priceVariant} formatCurrency={formatCurrency} />
  );

  return (
    <div className={twMerge('flex w-fit items-center gap-4', !applyClassesToButton && className)}>
      {pricePosition === 'left' && price}
      <Button
        variant={variant}
        href={hasExternalAreaLink ? undefined : href}
        openInNewTab={hasExternalAreaLink ? undefined : openInNewTab}
        scroll={parsedLink?.scroll}
        className={twJoin(noUppercase && 'normal-case', applyClassesToButton && className)}
        onClick={onClick}
        aria-label={ariaLabel}
        data-tile-element-id={tileElementTrackingId}
      >
        {text}
        {children}
      </Button>
      {pricePosition === 'right' && price}
      {modal}
    </div>
  );
}
